/**
 * PointBalance primitive (Plan 02-11 / UI-SPEC §"<PointBalance>").
 *
 * Saldotallet på Lommebok (/poeng): 56 px, ekstra fet, tette tall (#156,
 * steg 6e; Stell_Lommebok). When `countUpFrom` differs
 * from `balance`, animates the digit increment via requestAnimationFrame
 * over 700ms (UI-SPEC token --duration-celebration-l3-countup). Plan 15
 * (celebrations) triggers count-up by mounting with countUpFrom set to the
 * previous balance.
 *
 * Reduced-motion: when prefers-reduced-motion=reduce, the animation is
 * skipped and `shown` is set to `balance` synchronously. We intentionally
 * read `useReducedMotion()` from motion v12 (matches BottomSheet pattern)
 * so a reduced-motion user never sees a count-up.
 *
 * `formatPoints` (i18n/formatters) renders the Norwegian thousands separator
 * (NBSP). The component never directly calls Intl.* — all locale-aware
 * formatting flows through the shared formatters module (HIGH-4).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatPoints } from '../../i18n/formatters';

const COUNTUP_MS = 700; // --duration-celebration-l3-countup (Plan 15 will materialise the token)

/**
 * Read prefers-reduced-motion synchronously at module/call time. We avoid
 * `motion/react`'s `useReducedMotion()` here because v12 returns `null`
 * before the matchMedia listener fires on the first render — that nullable
 * tri-state would let a count-up start for one frame on a reduced-motion
 * user before snapping. Reading `matchMedia` directly gives us a sync
 * boolean for the initial-state decision; the live tracking via effect is
 * unnecessary because count-up runs once per balance change.
 */
function isReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface PointBalanceProps {
  balance: number;
  /**
   * Previous balance to animate up from. When undefined or === balance, no
   * animation. Explicitly `| undefined` so a caller may pass the prop through
   * unconditionally under exactOptionalPropertyTypes — WalletRoute computes it
   * per render from a possibly-absent count-up intent (issue #79).
   */
  countUpFrom?: number | undefined;
  className?: string;
}

export function PointBalance({
  balance,
  countUpFrom,
  className,
}: PointBalanceProps): ReactNode {
  // Compute initial-state synchronously: animate only when an explicit
  // countUpFrom !== balance is provided AND the user hasn't asked for
  // reduced-motion. Otherwise the very first render already shows balance.
  const willAnimate =
    countUpFrom !== undefined && countUpFrom !== balance && !isReducedMotion();
  const [shown, setShown] = useState<number>(willAnimate ? countUpFrom : balance);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // No animation requested → snap to balance.
    if (countUpFrom === undefined || countUpFrom === balance) {
      setShown(balance);
      return undefined;
    }
    // Reduced-motion → snap to balance, never call rAF.
    if (isReducedMotion()) {
      setShown(balance);
      return undefined;
    }
    // rAF animation: cubic-out easing across COUNTUP_MS.
    const start = performance.now();
    const from = countUpFrom;
    const to = balance;
    const step = (now: number): void => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / COUNTUP_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(from + (to - from) * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [balance, countUpFrom]);

  return (
    <span
      data-testid="point-balance"
      className={[
        'tnum text-display-m leading-tight font-extrabold tracking-tighter',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {formatPoints(shown)}
    </span>
  );
}
